import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AuthRole } from '@libs/types';
import { useLazyJobseekerCheckProfileCompletenessQuery } from '@carcareers/store';
import { useAuth } from './AuthContext';
import { CompleteProfileNotification } from '@carcareers/components';

interface NotificationData {
  profileCompleteness: {
    isComplete: boolean | null;
    hasChecked: boolean;
  };
}

interface NotificationContextProps {
  notifications: NotificationData;
  checkProfileCompleteness: () => void;
  isProfileComplete: boolean | null;
  hasCheckedProfile: boolean;
  checkProfile: () => void;
}

const NotificationContext = createContext<NotificationContextProps | null>(null);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [isProfileComplete, setIsProfileComplete] = useState<boolean | null>(null);
  const [hasCheckedProfile, setHasCheckedProfile] = useState(false);
  const [checkProfileCompleteness, { data, isLoading, error }] = useLazyJobseekerCheckProfileCompletenessQuery();

  const checkProfile = () => {
    if (user?.role === AuthRole.JOBSEEKER && !hasCheckedProfile) {
      checkProfileCompleteness();
      setHasCheckedProfile(true);
    }
  };

  useEffect(() => {
    if (user?.role === AuthRole.JOBSEEKER) {
      const justLoggedIn = sessionStorage.getItem('checkTOS') === 'true';
      
      if (justLoggedIn && !hasCheckedProfile) {
        checkProfileCompleteness();
        setHasCheckedProfile(true);
      }
    }
  }, [user, hasCheckedProfile, checkProfileCompleteness]);

  useEffect(() => {
    if (data) {
      setIsProfileComplete(data.isComplete);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      console.error('Error checking profile completeness:', error);
    }
  }, [error]);

  const notifications: NotificationData = {
    profileCompleteness: {
      isComplete: isProfileComplete,
      hasChecked: hasCheckedProfile,
    },
  };

  const value = {
    notifications,
    checkProfileCompleteness: checkProfile,
    isProfileComplete,
    hasCheckedProfile,
    checkProfile,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <CompleteProfileNotification />
    </NotificationContext.Provider>
  );
};
